/**
 * Shared PDF export helper — used by every page that ships a PDF
 * (Tax Account Audit, Year-End Report, future Risk Trend exports,
 * etc.). Centralising it means a single bug fix or layout tweak
 * applies everywhere.
 *
 * Layout hardening:
 *   • Adds `pdf-rendering` to <body> for the duration of the capture
 *     so the global CSS rules in index.css can:
 *       - collapse multi-column grids to single column (cards take
 *         full page width, badges don't wrap)
 *       - force `page-break-inside: avoid` on `.print-clean` cards
 *       - force `white-space: nowrap` on tiny badge text so labels
 *         like "NIIT-EXEMPT" never break onto two lines
 *   • Default `pagebreak.avoid` includes `.print-clean` rows, tables
 *     rows, and any element opted-in with `data-pdf-keep`.
 *
 * Footer stamp:
 *   • Every page is post-stamped with
 *       "Generated for {user} on {date} · twylightcrow.com · {n}/{N}"
 *     via the underlying jsPDF instance. Centralised so a single
 *     edit changes the brand stamp on every PDF the app emits.
 *
 * Round 92o++++++++N++++++++ (May 2026) — extracted from the
 * duplicate handlePdf functions in TaxAccountAudit.jsx and
 * YearEndReport.js after user-reported badge wrap + ticker cut-off
 * bugs in the Roth IRA section of the Tax Account Audit PDF.
 */
import { toast } from 'sonner';

const PDF_BODY_CLASS = 'pdf-rendering';
const PDF_FOOTER_BRAND = 'twylightcrow.com';

/**
 * Build the "Generated for X on DATE · twylightcrow.com · n/N"
 * footer text for a single page. Kept here (not in the page-level
 * `handlePdf`) so every export inherits the same stamp without
 * each caller re-implementing it.
 */
function _buildFooterText({ user, pageNumber, pageCount, brand = PDF_FOOTER_BRAND }) {
  const name = (user && (user.name || user.email)) || 'DiviDrip user';
  const now = new Date();
  const date = now.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  return `Generated for ${name} on ${date}  ·  ${brand}  ·  page ${pageNumber} of ${pageCount}`;
}

/**
 * Stamp the footer on every page of the jsPDF instance. Uses the
 * library's pixel-coordinate API (unit: mm) and a small grey
 * Helvetica so it never visually clashes with the page content.
 */
function _stampFooter(pdf, user) {
  try {
    const totalPages = pdf.internal.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    for (let i = 1; i <= totalPages; i += 1) {
      pdf.setPage(i);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(120, 120, 130);
      const text = _buildFooterText({
        user,
        pageNumber: i,
        pageCount: totalPages,
      });
      // 4mm above the bottom edge — sits inside the 12mm bottom
      // margin we configure in `opts` below so it never collides
      // with content.
      pdf.text(text, pageWidth / 2, pageHeight - 4, { align: 'center' });
    }
  } catch (_) {
    // Footer is decorative — never let a stamping failure abort
    // the actual PDF export.
  }
}

/**
 * Generate a PDF from a DOM node.
 *
 * @param {Object} params
 * @param {string} params.elementId          — DOM id of the node to capture.
 * @param {string} params.filename           — Suggested download filename.
 * @param {('download'|'newtab'|'both')} params.mode
 * @param {string} [params.headerSelector]   — CSS selector of a header
 *   element inside the captured node that is hidden in web view and
 *   should be revealed only during PDF capture (e.g. `.ta-pdf-header`).
 * @param {Object} [params.user]             — Logged-in user object;
 *   used to build the "Generated for {name}" footer stamp. Falls
 *   back to "DiviDrip user" when null.
 * @param {Function} [params.onAfter]        — Called after success (e.g.
 *   show a referral toast).
 * @returns {Promise<boolean>}               — true on success, false on
 *   error (caller can use to flip a `pdfBusy` state).
 */
export async function exportNodeAsPdf({
  elementId,
  filename,
  mode = 'download',
  headerSelector = null,
  user = null,
  onAfter = null,
}) {
  const node = document.getElementById(elementId);
  if (!node) {
    toast.error('PDF content not ready — wait a moment and try again.');
    return false;
  }

  // Round 93p (Jun 2026) — popup-blocker fix.
  // CRITICAL: open the new tab SYNCHRONOUSLY here, BEFORE any
  // `await`s consume the user-gesture trust. If we waited until
  // after the async `import('html2pdf.js')` + `worker.toPdf()` chain
  // (which takes 1-3s), the browser would have long since dropped
  // the click-gesture context and would block `window.open()` as
  // an unsolicited popup. We park the tab on about:blank with a
  // tiny "Building PDF…" placeholder, then navigate it to the blob
  // URL once the PDF is ready.
  let parkedTab = null;
  if (mode === 'newtab' || mode === 'both') {
    parkedTab = window.open('about:blank', '_blank');
    if (!parkedTab) {
      toast.error(
        'Browser blocked the new tab. Allow pop-ups for this site or use Download instead.',
      );
      return false;
    }
    try {
      parkedTab.document.write(
        `<!doctype html><meta charset="utf-8"><title>Building PDF…</title>` +
        `<style>body{margin:0;background:#0b1020;color:#e6e9f5;font:14px system-ui;` +
        `display:flex;align-items:center;justify-content:center;height:100vh}` +
        `.s{display:flex;flex-direction:column;gap:8px;align-items:center}` +
        `.d{width:28px;height:28px;border:3px solid #4b5b9c;border-top-color:#a5b4fc;` +
        `border-radius:50%;animation:r .8s linear infinite}` +
        `@keyframes r{to{transform:rotate(360deg)}}</style>` +
        `<div class="s"><div class="d"></div><div>Building PDF…</div></div>`,
      );
      parkedTab.document.close();
    } catch {
      /* writing to about:blank can fail in obscure setups — that's OK,
         the tab will just be blank until we navigate it. */
    }
  }

  const toastId = toast.loading('Building PDF…');
  const pdfHeader = headerSelector ? node.querySelector(headerSelector) : null;
  if (pdfHeader) pdfHeader.classList.remove('hidden');
  document.body.classList.add(PDF_BODY_CLASS);

  let blobUrl = null;
  try {
    const mod = await import('html2pdf.js');
    const html2pdf = mod.default || mod;
    const opts = {
      margin: [10, 10, 14, 10],
      filename,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: {
        mode: ['css', 'legacy'],
        avoid: [
          '.print-clean',
          '[data-pdf-keep]',
          'tr',
          'li',
          'p',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          '.sec-filing-html > *',
          '.sec-filing-html div',
          '.sec-filing-html font',
        ],
      },
    };
    const worker = html2pdf().from(node).set(opts);
    const pdf = await worker.toPdf().get('pdf');
    _stampFooter(pdf, user);
    const blob = pdf.output('blob');
    blobUrl = URL.createObjectURL(blob);

    if (parkedTab) {
      // The tab may have been closed by the user during the 1-3s
      // build. If still open, navigate it to the freshly-built blob.
      if (!parkedTab.closed) {
        try { parkedTab.location.href = blobUrl; } catch { /* ignore */ }
      }
    }
    if (mode === 'download' || mode === 'both') {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    const label =
      mode === 'both' ? 'PDF saved & opened.' :
      mode === 'newtab' ? 'PDF opened in a new tab.' :
      'PDF downloaded.';
    toast.success(label, { id: toastId });
    if (typeof onAfter === 'function') {
      try { onAfter(); } catch { /* ignore */ }
    }
    return true;
  } catch (e) {
    if (parkedTab && !parkedTab.closed) {
      try { parkedTab.close(); } catch { /* ignore */ }
    }
    toast.error(`Couldn't build PDF: ${e?.message || 'unknown error'}`, { id: toastId });
    return false;
  } finally {
    document.body.classList.remove(PDF_BODY_CLASS);
    if (pdfHeader) pdfHeader.classList.add('hidden');
    if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  }
}
